import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { LANG_CODES, stripLang, withLang, hreflangCode, extractCitySlug, cityPath } from "../i18n/langPath";
import { LANDING_ROUTES, landingLangs } from "../data/landingRoutes";
import { isKnownCity } from "../data/cities";

const SITE = "https://zuerichairporttaxi.ch";

// Global canonical + hreflang alternates for the current route across all languages.
// City landing pages use localized keyword slugs, so alternates are built per-language.
export default function HreflangTags() {
  const { pathname } = useLocation();
  const { lang, path } = stripLang(pathname);
  // Language-specific keyword landing pages are self-canonical to their own
  // language only (the slug wording is native to one language) — this prevents
  // same-language duplicate/doorway pages.
  const kwSlug = (path || "/").replace(/^\//, "");
  // The keyword-landing lookup table is heavy (~160KB), so it is loaded lazily
  // (never on the homepage) to keep it OUT of the main bundle. The prerendered
  // static HTML already carries the correct canonical, so SEO is unaffected.
  const [kwLang, setKwLang] = useState(null);
  useEffect(() => {
    let active = true;
    if (!kwSlug) { setKwLang(null); return; }
    import("../data/keywordLandings").then((m) => {
      if (active) setKwLang(m.getKeywordLang(kwSlug) || null);
    });
    return () => { active = false; };
  }, [kwSlug]);

  if (kwLang) {
    const href = `${SITE}${withLang(`/${kwSlug}`, kwLang)}`;
    return (
      <Helmet>
        <link rel="canonical" href={href} />
        <link rel="alternate" hrefLang={hreflangCode(kwLang)} href={href} />
        <link rel="alternate" hrefLang="x-default" href={href} />
      </Helmet>
    );
  }
  // SEO landings: self-canonical in their languages (default de + en); others point to EN.
  if (LANDING_ROUTES.includes(kwSlug)) {
    const href = (l) => `${SITE}${withLang(`/${kwSlug}`, l)}`;
    return (
      <Helmet>
        <link rel="canonical" href={href(landingLangs(kwSlug).includes(lang) ? lang : "en")} />
        {landingLangs(kwSlug).map((l) => <link key={l} rel="alternate" hrefLang={l} href={href(l)} />)}
        <link rel="alternate" hrefLang="x-default" href={href("en")} />
      </Helmet>
    );
  }
  const citySlug = extractCitySlug(path, lang);
  if (citySlug && !isKnownCity(citySlug)) return null; // unknown city: noindex page, no canonical/hreflang
  const altFor = (code) => (citySlug ? cityPath(citySlug, code) : withLang(path, code));
  const canonical = `${SITE}${altFor(lang)}`;
  return (
    <Helmet>
      <link rel="canonical" href={canonical} />
      {LANG_CODES.map((code) => (
        <link key={code} rel="alternate" hrefLang={hreflangCode(code)} href={`${SITE}${altFor(code)}`} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${SITE}${altFor("en")}`} />
    </Helmet>
  );
}
